// [VP-IDLE-LOGOUT 2026-05-27]
//
// Auto-logout after IDLE_TIMEOUT_MS of inactivity. Mouse moves, mouse clicks,
// keystrokes, scrolls and touches all reset the timer — so the countdown only
// elapses when the user has truly done nothing for that long.
//
// Mounted once at the root (App.js). When the timer fires, all auth cookies
// are cleared and the existing window 'logout' event is dispatched, which the
// App-level listener already handles (state reset + redirect to /login).
//
// Tune IDLE_TIMEOUT_MS to taste — it's the single knob for this feature.
//
// ROLLBACK: `grep -rn "VP-IDLE-LOGOUT" bbox/src` finds every line of this fix
// (this file, App.js, auth/Login.js).

import { useEffect, useRef } from 'react';
import Cookies from 'js-cookie';

// ▶ Single tunable: idle window before auto-logout. Examples:
//     10 * 60 * 1000 →  10 min
//     15 * 60 * 1000 →  15 min (current)
//     30 * 60 * 1000 →  30 min
export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

// Cookies cleared on idle logout. Mirror everything auth/Login.js sets.
const AUTH_COOKIES = [
  'access_token', 'csrf_token',
  'user_role', 'user_role_label', 'user_role_pages',
  'user_id', 'email',
  'crm_user_id', 'crm_role',
  'dealer_id', 'dealer_code',
  'last_login',
];

// Events that count as user activity (reset the idle countdown).
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export const useIdleLogout = (timeoutMs = IDLE_TIMEOUT_MS) => {
  const timerRef = useRef(null);

  useEffect(() => {
    const fire = () => {
      // Nothing to log out from — harmless no-op while on the /login page.
      if (!Cookies.get('access_token')) return;
      AUTH_COOKIES.forEach((c) => Cookies.remove(c));
      window.dispatchEvent(new Event('logout'));
    };

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fire, timeoutMs);
    };

    reset(); // start the countdown on mount
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, reset));
    };
  }, [timeoutMs]);
};
